import { FormikActions, FormikProps } from 'formik';
import React, { Fragment, PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Element, scroller } from 'react-scroll';
import * as yup from 'yup';
import { FormContact, SendFormResult, ServiceResult } from '../../../bzit';
import { Column, Row } from '../../../components/Grid';
import { ContentHeader } from '../../../components/layout/ContentHeader';
import { Section } from '../../../components/Section';
import { scrollOptions } from '../../../configs';
import { reportError } from '../../../error-handling/reportError';
import { sendContact } from '../../../helper/fetch/ContactFormService';
import { submittedContactForm } from '../../../provider/TrackingProvider/conversions/submittedContactForm';
import { withTrackingApi, WithTrackingApiProps } from '../../../provider/TrackingProvider/withTrackingApi';
import { CType } from '../../../provider/Typo3Provider/types';
import Typo3Label from '../../../provider/Typo3Provider/Typo3Label';
import {
  withTypo3Translation,
  WithTypo3TranslationProps
} from '../../../provider/Typo3Provider/withTypo3Translation';
import { FormInput } from '../../FormContainer/components/FormInput';
import Select from '../../FormContainer/components/Select';
import ContactForm from '../../FormContainer/ContactForm';
import LocationOptions from '../../FormContainer/LocationOptions';

type FormContactContainerProps = CType;

interface FormContactContainerState {
  submitted: boolean;
}

type FormContactType = FormContact & {
  codeLocation: string;
};

const validationSchema = {
  codeLocation: yup.string().required()
};

class FormContactContainer extends PureComponent<
  FormContactContainerProps & RouteComponentProps & WithTypo3TranslationProps & WithTrackingApiProps,
  FormContactContainerState
> {
  public state = {
    submitted: false
  };

  private getCodeLocation(): string | undefined {
    return (this.props.location.state || {} as any).codeLocation || undefined;
  }

  public getInitialCodeLocation() {
    return this.getCodeLocation() || '';
  }

  public getKeyFromLocationState() {
    return this.getCodeLocation() || 'empty';
  }

  public sendForm = async (
    formData: FormContactType,
    { setSubmitting }: FormikActions<FormContactType>
  ) => {
    const { codeLocation, ...contact } = formData;
    try {
      const response: ServiceResult<SendFormResult> = await sendContact({
        codeLocation,
        contact
      });
      if (response.success) {
        this.setState({
          submitted: true
        });

        this.props.trackingApi.trackEvents(submittedContactForm());
      } else {
        throw response.error;
      }
    } catch (e) {
      reportError('FormContactContainer.sendForm()', e);
    }
  };

  public getCodeLocationForPrivacyInfo = ({
    codeLocation
  }: FormContactType) => {
    return codeLocation;
  };

  public componentDidUpdate(
    prevProps: FormContactContainerProps,
    prevState: FormContactContainerState
  ) {
    if (this.state.submitted && !prevState.submitted) {
      scroller.scrollTo('form-contact-top', scrollOptions);
    }
  }

  public renderForm = ({
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange
  }: FormikProps<FormContactType>) => {
    return (
      <FormInput
        label={<Typo3Label label="form.label.location" />}
        component={Select}
        name="codeLocation"
        value={values.codeLocation}
        error={!!errors.codeLocation && !!touched.codeLocation}
        onChange={handleChange}
        onBlur={handleBlur}
        required
      >
        <LocationOptions />
      </FormInput>
    );
  };

  public render() {
    const { header, subheader, typo3Translate } = this.props;
    const { submitted } = this.state;
    return (
      <Fragment>
        <Element name="form-contact-top" />
        {!submitted ? (
          <Section key={this.getKeyFromLocationState()}>
            <ContentHeader header={header} subheader={subheader} />
            <Row justifyContent="center">
              <Column large={6}>
                <ContactForm
                  onSubmit={this.sendForm}
                  renderBefore={this.renderForm}
                  initialExtraValues={{
                    codeLocation: this.getInitialCodeLocation()
                  }}
                  extraValidationSchema={validationSchema}
                  codeLocation={this.getCodeLocationForPrivacyInfo}
                />
              </Column>
            </Row>
          </Section>
        ) : (
          <Section>
            <ContentHeader
              header={typo3Translate(
                'form.submitted.header',
                null,
                'Vielen Dank für die Anfrage'
              )}
              subheader={typo3Translate(
                'form.submitted.body',
                null,
                'Wir haben Ihre Anfrage erhalten und werden uns schnellstmöglich bei Ihnen melden.'
              )}
            />
          </Section>
        )}
      </Fragment>
    );
  }
}

export default withRouter(withTrackingApi(withTypo3Translation(FormContactContainer)));
